/**
 * Tracks whether the most recent navigation was a browser back/forward
 * (popstate) navigation, as opposed to a forward navigation triggered by a
 * `<Link>` click or `router.push`.
 *
 * Why a module singleton instead of component state: the saved-pagination /
 * scroll-restoration guards live in two places that don't share a tree branch
 * — `ProductProvider` (top of the app) and `BrowseCategoryPage` (the page).
 * Both need to know "is this a back navigation?" so they only *preserve* the
 * previous list/scroll on back navigation and always *replace* it on a forward
 * navbar click. `router.beforePopState` can only hold a single handler (already
 * used by `useScrollRestoration`), so we listen to the native `popstate` event,
 * which supports multiple listeners and fires for browser back/forward only.
 *
 * The flag is reset shortly after the route change completes (see
 * `resetBackNavigationSoon`, wired from `RouterProvider`) — the small delay
 * keeps it readable during the post-navigation render where the guards run,
 * mirroring the timing in `useScrollRestoration`.
 */

let backNav = false;
let resetTimer: ReturnType<typeof setTimeout> | null = null;
// True only between a `popstate` firing and the `routeChangeStart` it triggers.
// `markRouteChangeStart` consumes it to decide the direction of THIS navigation,
// then clears it — so a later stray popstate can't pin `backNav` true forever.
let pendingPop = false;
// Like `backNav`, but held for the whole visit: true from the start of a
// back/forward navigation until the next navigation starts. `backNav` is
// cleared 150ms after arrival, too soon for fetches a page makes later.
let enteredByBack = false;
// When the popstate that started this visit fired (performance.now()), so a
// page can time how long Back took to put it back (utils/back-restore-report.ts).
let popAt = 0;
let enteredByBackAt: number | null = null;

if (typeof window !== 'undefined') {
  // A back/forward `popstate` always fires immediately before Next's
  // `routeChangeStart`. We only record that it happened; `markRouteChangeStart`
  // turns it into the per-navigation `backNav` value. This avoids the old bug
  // where popstate sources with no paired Next route change (iOS edge-swipe,
  // the in-app back button's `history.back()`, `changePath`'s direct
  // history.pushState) left `backNav` stuck true and made every subsequent
  // forward navigation skip its client-side fetch (broke mobile category
  // switching, which has no SSR list to fall back on).
  window.addEventListener('popstate', () => {
    pendingPop = true;
    popAt = performance.now();
  });
}

/**
 * Called from `RouterProvider` on every `routeChangeStart`. Sets `backNav` for
 * the navigation that's starting: true only when a `popstate` immediately
 * preceded it (browser/native back-forward), false for a forward
 * `<Link>` / `router.push`. Deterministic per navigation — never sticky.
 */
export const markRouteChangeStart = (): void => {
  backNav = pendingPop;
  enteredByBack = pendingPop;
  enteredByBackAt = pendingPop ? popAt : null;
  pendingPop = false;
  if (resetTimer) {
    clearTimeout(resetTimer);
    resetTimer = null;
  }
};

/**
 * True when the in-flight (or just-completed) navigation came from the browser
 * back/forward buttons. False for forward `<Link>` / `router.push` navigation.
 */
export const isBackNavigation = (): boolean => backNav;

/**
 * True for the whole visit to a page reached by browser back/forward, until
 * the next navigation starts. For work that happens after arrival settles,
 * such as the fetches a page makes as the reader scrolls.
 */
export const isPageEnteredByBack = (): boolean => enteredByBack;

/**
 * When the Back that entered this page was pressed, as `performance.now()`;
 * null on a page reached any other way. Also tells one Back visit from the next.
 */
export const pageEnteredByBackAt = (): number | null => enteredByBackAt;

/**
 * Clear the back-navigation flag shortly after a route change completes. Called
 * from `RouterProvider`'s route-change handler. The delay ensures the flag is
 * still true while the guards in `ProductProvider` / `BrowseCategoryPage` run
 * during the post-navigation render.
 */
export const resetBackNavigationSoon = (): void => {
  if (resetTimer) clearTimeout(resetTimer);
  resetTimer = setTimeout(() => {
    backNav = false;
    resetTimer = null;
  }, 150);
};
